import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api/axiosConfig';
import { useModal } from '../contexts/ModalContext';

const ChecklistManagementPage = () => {
    const navigate = useNavigate();
    const [selectedChecklists, setSelectedChecklists] = useState(new Set());
    const [checklists, setChecklists] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const { showAlert } = useModal();

    useEffect(() => {
        const fetchChecklists = async () => {
            try {
                const res = await api.get('/checklist-templates');
                const formattedChecklists = res.data.map(t => {
                    return {
                        id: t.id,
                        name: t.name,
                        // 이 점검표로 도는 시설. 예전에는 구역이 점검표를 들고
                        // 있어 거기서 셌는데, 이제 정하는 것은 순회 경로다.
                        facilityCount: new Set(
                            (t.routeStops || []).map(s => s.facilityId)
                        ).size
                    };
                });
                setChecklists(formattedChecklists);
            } catch (err) {
                console.error("Failed to fetch checklists", err);
            } finally {
                setLoading(false);
            }
        };
        fetchChecklists();
    }, []);

    const filteredChecklists = checklists.filter(c =>
        c.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const toggleSelect = (id) => {
        const newSet = new Set(selectedChecklists);
        if (newSet.has(id)) newSet.delete(id);
        else newSet.add(id);
        setSelectedChecklists(newSet);
    };

    const handleDelete = async () => {
        if (selectedChecklists.size === 0) return;
        if (!window.confirm(`${selectedChecklists.size}개의 점검표를 삭제하시겠습니까?`)) return;

        try {
            await Promise.all(Array.from(selectedChecklists).map(id => api.delete(`/checklist-templates/${id}`)));
            setChecklists(prev => prev.filter(c => !selectedChecklists.has(c.id)));
            setSelectedChecklists(new Set());
            showAlert('성공', '선택한 점검표가 삭제되었습니다.', 'success');
        } catch (err) {
            console.error("Failed to delete checklists", err);
            showAlert('오류', '점검표 삭제 중 문제가 발생했습니다.', 'danger');
        }
    };

    return (
        <div className="pb-5">
            <div className="checklist-settings">
                <div className="d-flex justify-content-between align-items-start mb-4">
                    <div>
                        <h4 className="fw-bold mb-1">점검표 관리</h4>
                        <p className="text-muted small mb-0">
                            점검 주기와 항목을 설정하여 관리 현장에 배정된 점검표들을 관리합니다.
                        </p>
                    </div>
                    <Link to="/checklists/new" className="btn btn-primary rounded-3 px-3 py-2">
                        <i className="bi bi-plus-lg"></i>
                        <span className="d-none d-md-inline ms-2">새 점검표 추가</span>
                    </Link>
                </div>

                <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                    <div className="card-header bg-white border-bottom-0 pt-4 px-4">
                        <div className="d-flex justify-content-between align-items-center">
                            <span className="fw-bold text-primary text-nowrap me-3">전체 {filteredChecklists.length}건</span>
                            <div className="input-group input-group-sm" style={{ maxWidth: '250px' }}>
                                <span className="input-group-text bg-light border-0"><i className="bi bi-search"></i></span>
                                <input
                                    type="text"
                                    className="form-control bg-light border-0"
                                    placeholder="점검표 검색..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="card-body p-0">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : (
                            <>
                                {/* Desktop View - Table */}
                                <div className="table-responsive d-none d-md-block">
                                    <table className="table table-hover align-middle mb-0">
                                        <thead className="bg-light text-muted small uppercase">
                                            <tr>
                                                <th scope="col" className="ps-4" style={{ width: '40px' }}>
                                                    <div className="form-check">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            checked={selectedChecklists.size === filteredChecklists.length && filteredChecklists.length > 0}
                                                            onChange={() => {
                                                                if (selectedChecklists.size === filteredChecklists.length) setSelectedChecklists(new Set());
                                                                else setSelectedChecklists(new Set(filteredChecklists.map(c => c.id)));
                                                            }}
                                                        />
                                                    </div>
                                                </th>
                                                <th scope="col" className="text-nowrap">점검표 이름</th>
                                                <th scope="col" className="text-center text-nowrap">배정 시설</th>
                                            </tr>
                                        </thead>
                                        <tbody className="border-top-0">
                                            {filteredChecklists.length > 0 ? (
                                                filteredChecklists.map(checklist => (
                                                    <tr
                                                        key={checklist.id}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        <td className="ps-4 py-3" onClick={(e) => e.stopPropagation()}>
                                                            <div className="form-check">
                                                                <input
                                                                    className="form-check-input"
                                                                    type="checkbox"
                                                                    checked={selectedChecklists.has(checklist.id)}
                                                                    onChange={() => toggleSelect(checklist.id)}
                                                                />
                                                            </div>
                                                        </td>
                                                        <td onClick={() => navigate(`/checklists/${checklist.id}`)}>
                                                            <div className="fw-bold text-dark text-nowrap">{checklist.name}</div>
                                                        </td>
                                                        <td className="text-center text-nowrap" onClick={() => navigate(`/checklists/${checklist.id}`)}>
                                                            <span className="badge bg-light text-primary border border-primary-subtle rounded-pill px-3">
                                                                {checklist.facilityCount}개
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="3" className="text-center py-5 text-muted">
                                                        데이터가 없습니다.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Mobile View - Cards */}
                                <div className="d-md-none py-3 bg-light">
                                    {filteredChecklists.length > 0 ? (
                                        filteredChecklists.map(checklist => (
                                            <div
                                                key={checklist.id}
                                                className="card border-0 shadow-sm mb-3"
                                                onClick={() => navigate(`/checklists/${checklist.id}`)}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <div className="card-body">
                                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                                        <div className="d-flex align-items-center gap-2">
                                                            <div onClick={(e) => e.stopPropagation()}>
                                                                <input
                                                                    className="form-check-input"
                                                                    type="checkbox"
                                                                    checked={selectedChecklists.has(checklist.id)}
                                                                    onChange={() => toggleSelect(checklist.id)}
                                                                />
                                                            </div>
                                                            <div className="fw-bold text-dark">{checklist.name}</div>
                                                        </div>
                                                    </div>
                                                    <div className="ps-4 ms-1">
                                                        <div className="text-secondary small">
                                                            <span className="badge bg-light text-primary border border-primary-subtle rounded-pill px-3">
                                                                배정 시설 {checklist.facilityCount}개
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="text-center py-5 text-muted">
                                            데이터가 없습니다.
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>

                    <div className="card-footer bg-white border-top-0 py-4 px-4">
                        <div className="d-flex justify-content-between align-items-center">
                            <button
                                className="btn btn-outline-danger btn-sm rounded-3"
                                disabled={selectedChecklists.size === 0}
                                onClick={handleDelete}
                            >
                                선택 삭제 ({selectedChecklists.size})
                            </button>
                            <nav aria-label="Page navigation">
                                <ul className="pagination pagination-sm mb-0">
                                    <li className="page-item disabled">
                                        <button className="page-link rounded-start-3 border-0 bg-light"><i className="bi bi-chevron-left"></i></button>
                                    </li>
                                    <li className="page-item active"><button className="page-link border-0">1</button></li>
                                    <li className="page-item">
                                        <button className="page-link rounded-end-3 border-0 bg-light"><i className="bi bi-chevron-right"></i></button>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChecklistManagementPage;
